import { setAttribute } from './utils';
var DOMSerializer = /** @class */ (function () {
    /// Create a serializer. `nodes` should map node names to functions
    /// that take a node and return a description of the corresponding
    /// DOM. `marks` does the same for mark names, but also gets an
    /// argument that tells it whether the mark's content is block or
    /// inline content (for typical use, it'll always be inline). A mark
    /// serializer may be `null` to indicate that marks of that type
    /// should not be serialized.
    function DOMSerializer(
    /// The node serialization functions.
    nodes, 
    /// The mark serialization functions.
    marks) {
        this.nodes = nodes;
        this.marks = marks;
    }
    /// Render an [output spec](#model.DOMOutputSpec) to a DOM node. If
    /// the spec has a hole (zero) in it, `contentDOM` will point at the
    /// node with the hole.
    DOMSerializer.renderSpec = function (docum, structure, xmlNS) {
        if (xmlNS === void 0) { xmlNS = null; }
        if (typeof structure === 'string') {
            return { dom: docum.createTextNode(structure) };
        }
        if (structure.nodeType != null) {
            return { dom: structure };
        }
        if (structure.dom && structure.dom.nodeType != null) {
            return structure;
        }
        var tagName = structure[0], space = tagName.indexOf(' ');
        if (space > 0) {
            xmlNS = tagName.slice(0, space);
            tagName = tagName.slice(space + 1);
        }
        var contentDOM;
        var dom = (xmlNS ? docum.createElementNS(xmlNS, tagName) : docum.createElement(tagName));
        var attrs = structure[1], start = 1;
        if (attrs && typeof attrs === 'object' && attrs.nodeType == null && !Array.isArray(attrs)) {
            start = 2;
            for (var name_1 in attrs) {
                if (attrs[name_1] != null) {
                    space = name_1.indexOf(' ');
                    if (space > 0) {
                        dom.setAttributeNS(name_1.slice(0, space), name_1.slice(space + 1), attrs[name_1]);
                    }
                    else {
                        setAttribute(dom, name_1, attrs[name_1]);
                    }
                }
            }
        }
        for (var i = start; i < structure.length; i++) {
            var child = structure[i];
            if (child === 0) {
                if (i < structure.length - 1 || i > start) {
                    throw new RangeError('Content hole must be the only child of its parent node');
                }
                return { dom: dom, contentDOM: dom };
            }
            else {
                var _a = DOMSerializer.renderSpec(docum, child, xmlNS), inner = _a.dom, innerContent = _a.contentDOM;
                dom.appendChild(inner);
                if (innerContent) {
                    if (contentDOM) {
                        throw new RangeError('Multiple content holes');
                    }
                    contentDOM = innerContent;
                }
            }
        }
        return { dom: dom, contentDOM: contentDOM };
    };
    /// Build a serializer using the [`toDOM`](#model.NodeSpec.toDOM)
    /// properties in a schema's node and mark specs.
    DOMSerializer.fromSchema = function (schema) {
        return schema.cached.domSerializer ||
            (schema.cached.domSerializer = new DOMSerializer(this.nodesFromSchema(schema), this.marksFromSchema(schema)));
    };
    /// Gather the serializers in a schema's node specs into an object.
    /// This can be useful as a base to build a custom serializer from.
    DOMSerializer.nodesFromSchema = function (schema) {
        var result = gatherToDOM(schema.nodes);
        if (!result.text) {
            result.text = function (node) { return node.text; };
        }
        return result;
    };
    /// Gather the serializers in a schema's mark specs into an object.
    DOMSerializer.marksFromSchema = function (schema) {
        return gatherToDOM(schema.marks);
    };
    /// Serialize the content of this fragment to a DOM fragment. When
    /// not in the browser, the `document` option, containing a DOM
    /// document, should be passed so that the serializer can create
    /// nodes.
    DOMSerializer.prototype.serializeFragment = function (fragment, options, target) {
        var _this = this;
        if (options === void 0) { options = {}; }
        if (!target) {
            target = doc(options).createDocumentFragment();
        }
        var top = target, active = [];
        fragment.forEach(function (node) {
            if (active.length || node.marks.length) {
                var keep = 0, rendered = 0;
                while (keep < active.length && rendered < node.marks.length) {
                    var next = node.marks[rendered];
                    if (!_this.marks[next.type.name]) {
                        rendered++;
                        continue;
                    }
                    if (!next.eq(active[keep][0]) || next.type.spec.spanning === false) {
                        break;
                    }
                    keep++;
                    rendered++;
                }
                while (keep < active.length) {
                    top = active.pop()[1];
                }
                while (rendered < node.marks.length) {
                    var add = node.marks[rendered++];
                    var markDOM = _this.serializeMark(add, node.isInline, options);
                    if (markDOM) {
                        active.push([add, top]);
                        top.appendChild(markDOM.dom);
                        top = markDOM.contentDOM || markDOM.dom;
                    }
                }
            }
            top.appendChild(_this.serializeNodeInner(node, options));
        });
        return target;
    };
    /// Serialize this node to a DOM node. This can be useful when you
    /// need to serialize a part of a document, as opposed to the whole
    /// document. To serialize a whole document, use
    /// [`serializeFragment`](#model.DOMSerializer.serializeFragment) on
    /// its [content](#model.Node.content).
    DOMSerializer.prototype.serializeNode = function (node, options) {
        if (options === void 0) { options = {}; }
        var dom = this.serializeNodeInner(node, options);
        for (var i = node.marks.length - 1; i >= 0; i--) {
            var wrap = this.serializeMark(node.marks[i], node.isInline, options);
            if (wrap) {
                (wrap.contentDOM || wrap.dom).appendChild(dom);
                dom = wrap.dom;
            }
        }
        return dom;
    };
    /// @internal
    DOMSerializer.prototype.serializeMark = function (mark, inline, options) {
        if (options === void 0) { options = {}; }
        var toDOM = this.marks[mark.type.name];
        return toDOM && DOMSerializer.renderSpec(doc(options), toDOM(mark, inline));
    };
    /// @internal
    DOMSerializer.prototype.serializeNodeInner = function (node, options) {
        var _a = DOMSerializer.renderSpec(doc(options), this.nodes[node.type.name](node)), dom = _a.dom, contentDOM = _a.contentDOM;
        if (contentDOM) {
            if (node.isLeaf) {
                throw new RangeError('Content hole not allowed in a leaf node spec');
            }
            this.serializeFragment(node.content, options, contentDOM);
        }
        return dom;
    };
    return DOMSerializer;
}());
export { DOMSerializer };
function gatherToDOM(obj) {
    var result = {};
    for (var name_2 in obj) {
        if (obj[name_2]) {
            var toDOM = obj[name_2].spec.toDOM;
            if (toDOM) {
                result[name_2] = toDOM;
            }
        }
    }
    return result;
}
function doc(options) {
    return options.document || window.document;
}
